import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Select as ChakraSelect,
  Textarea,
  useToast,
  Text,
  Alert,
  AlertIcon,
  Icon,
  Box,
  Avatar,
} from '@chakra-ui/react';
import { FiUsers, FiUser } from 'react-icons/fi';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import ContactPicker from './ContactPicker';
import { useDisclosure } from '@chakra-ui/react';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import ContactTypeBadges from './ContactTypeBadges';

/**
 * Add Network Relationship Modal
 * 
 * Create general, non-deal-specific relationships between contacts.
 * Examples: referral partner, preferred vendor, sphere of influence
 * 
 * Features:
 * - Relationship type selector
 * - Contact picker
 * - Notes field
 * - Auto-creates reciprocal relationship
 */
export default function AddNetworkRelationshipModal({
  isOpen,
  onClose,
  contactId,
  existingRelationships = [],
  onRelationshipAdded,
}) {
  const toast = useToast();
  const { networkRelationshipTypes } = useCrmSettings();
  
  const [relationshipType, setRelationshipType] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [notes, setNotes] = useState('');
  const [strength, setStrength] = useState('medium');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { isOpen: isContactPickerOpen, onOpen: onContactPickerOpen, onClose: onContactPickerClose } = useDisclosure();

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRelationshipType('');
      setSelectedContact(null);
      setNotes('');
      setStrength('medium');
    }
  }, [isOpen]);

  const handleContactSelected = (contact) => {
    setSelectedContact(contact);
    onContactPickerClose();
  };

  const handleSubmit = async () => {
    if (!relationshipType) {
      toast({
        title: 'Select relationship type',
        description: 'Please select how this contact is related',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!selectedContact) {
      toast({
        title: 'Select a contact',
        description: 'Please select a contact to connect',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Check for duplicate
    const duplicate = existingRelationships.find(
      r => r.relatedContactId === selectedContact.id && r.relationshipType === relationshipType
    );

    if (duplicate) {
      toast({
        title: 'Relationship exists',
        description: 'This relationship already exists',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (!isSupabaseConfigured()) {
      toast({ title: 'Database not configured', status: 'error', duration: 5000 });
      return;
    }

    setIsSubmitting(true);
    try {
      // Insert the primary relationship
      const { data: newRelationship, error } = await supabase
        .from('crm_contact_relationships')
        .insert({
          contact_id: contactId,
          related_contact_id: selectedContact.id,
          relationship_type: relationshipType,
          notes: notes || null,
        })
        .select()
        .single();

      if (error) throw error;

      // Create reciprocal relationship if configured
      if (relationshipTypeConfig?.reciprocal) {
        const { error: reciprocalError } = await supabase
          .from('crm_contact_relationships')
          .insert({
            contact_id: selectedContact.id,
            related_contact_id: contactId,
            relationship_type: relationshipTypeConfig.reciprocal,
            notes: notes || null,
          });
        
        // Log but don't fail if reciprocal already exists (unique constraint)
        if (reciprocalError && reciprocalError.code !== '23505') {
          console.warn('Could not create reciprocal relationship:', reciprocalError);
        }
      }

      toast({
        title: 'Relationship added',
        description: 'Network relationship created successfully',
        status: 'success',
        duration: 2000,
      });

      if (onRelationshipAdded) {
        onRelationshipAdded(newRelationship);
      }

      onClose();
    } catch (error) {
      console.error('Error adding network relationship:', error);
      toast({
        title: 'Error',
        description: error.code === '23505' ? 'This relationship already exists' : error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const relationshipTypeConfig = relationshipType 
    ? networkRelationshipTypes?.find(t => t.value === relationshipType) 
    : null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={2}>
              <Icon as={FiUsers} />
              <Text>Add to Network</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Alert status="info" size="sm" borderRadius="md">
                <AlertIcon />
                <Text fontSize="xs">
                  Network relationships are general connections not tied to specific deals (e.g., referral partners, sphere of influence).
                </Text>
              </Alert>

              {/* Relationship Type */}
              <FormControl isRequired>
                <FormLabel fontSize="sm">Relationship Type</FormLabel>
                <ChakraSelect
                  value={relationshipType}
                  onChange={(e) => {
                    setRelationshipType(e.target.value);
                    setSelectedContact(null);
                  }}
                  placeholder="Select relationship type..."
                >
                  {networkRelationshipTypes?.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </ChakraSelect>
                {relationshipTypeConfig && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Creates reciprocal: {relationshipTypeConfig.reciprocal?.replace(/-/g, ' ')}
                  </Text>
                )}
              </FormControl>

              {/* Select Contact */}
              {relationshipType && (
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Select Contact</FormLabel>
                  {selectedContact ? (
                    <Box
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <HStack justify="space-between">
                        <HStack spacing={2}>
                          <Avatar size="sm" name={`${selectedContact.firstName} ${selectedContact.lastName}`} />
                          <VStack align="flex-start" spacing={0}>
                            <Text fontWeight="semibold" fontSize="sm">
                              {selectedContact.firstName} {selectedContact.lastName}
                            </Text>
                            <ContactTypeBadges
                              contactTypes={selectedContact.contactTypes || []}
                              primaryContactType={selectedContact.primaryContactType}
                              size="xs"
                              maxDisplay={2}
                            />
                          </VStack>
                        </HStack>
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={onContactPickerOpen}
                        >
                          Change
                        </Button>
                      </HStack>
                    </Box>
                  ) : (
                    <Button
                      leftIcon={<FiUser />}
                      onClick={onContactPickerOpen}
                      variant="outline"
                      w="full"
                    >
                      Select Contact
                    </Button>
                  )}
                </FormControl>
              )}

              {/* Optional Fields */}
              {selectedContact && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Relationship Strength</FormLabel>
                    <ChakraSelect
                      value={strength}
                      onChange={(e) => setStrength(e.target.value)}
                    >
                      <option value="weak">Weak</option>
                      <option value="medium">Medium</option>
                      <option value="strong">Strong</option>
                    </ChakraSelect>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      How strong is this relationship? (for sorting/prioritizing)
                    </Text>
                  </FormControl>

                  <FormControl>
                    <FormLabel fontSize="sm">Notes (Optional)</FormLabel>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Additional context about this relationship..."
                      rows={3}
                      fontSize="sm"
                    />
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Adding..."
              isDisabled={!relationshipType || !selectedContact}
            >
              Add to Network
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Contact Picker */}
      {relationshipType && (
        <ContactPicker
          isOpen={isContactPickerOpen}
          onClose={onContactPickerClose}
          onSelectContact={handleContactSelected}
          excludeContactIds={[contactId, ...existingRelationships.map(r => r.relatedContactId)]}
          title="Select Related Contact"
        />
      )}
    </>
  );
}

